// src/frontend/.../Recruitment/Validation/section7.js
import errorTrackerV2 from "./Utils/ErrorTrackerV2";

export function isAnyYesSelectedS7(formData = {}) {
  return Object.keys(formData).some((key) => formData[key] === true);
}

/**
 * Pure validation:
 * - does NOT touch react-signature-canvas directly
 * - the component passes signatureIsEmpty from the ref
 */
export function validateSection7(formData = {}, ctx = {}) {
  const { signatureIsEmpty = true } = ctx;
  const newErrors = {};

  // console.log("Section 7 Validation - formData:", formData);

  const isEmptyObject =
    formData &&
    typeof formData === "object" &&
    !Array.isArray(formData) &&
    Object.keys(formData).length === 0;

  // If data is null/undefined/empty, all required fields are missing
  if (formData === null || formData === undefined || isEmptyObject) {
    newErrors["dataNotFound"] =
      "No data found for this section. Please open and save it first.";
    errorTrackerV2.setSectionErrors("section7", newErrors);
    return {
      ...newErrors,
      _isValid: false,
    };
  }

  // Helper to check if section has any "Yes" values
  const hasYesInSection = (sectionKeys) => {
    return sectionKeys.some((k) => formData[k] === true);
  };

  // Define which fields trigger additional info for each subsection
  const sectionHasYes = {
    "7_01": hasYesInSection([
      "criminalOffence",
      "criminalOffenceRelatedToFraud",
      "criminalOffenceRelatedToCompanies",
      "criminalProceedings",
      "subjectToCriminalOffence",
      "otherConvictions",
      "arrestedForCriminalOffence",
      "produceDocuments",
      "firmConvictedOfCriminalOffence",
      "firmSummonedChargedInvestigated",
      "firmSubjectOfCriminalProceedings",
      "firmOrderedToProduceDocuments",
    ]),
    "7_02": hasYesInSection([
      "judgementDebt",
      "partyToCivilProceedings",
      "awareOfProceedingsAganistYou",
      "moreThanOneSetOfProceedings",
      "claimDebtFromYou",
      "currentJudgementDebt",
      "failedToSatisfyJudgementDebt",
      "filedForBankruptcy",
      "adjudgedBankrupt",
      "subjectOfBankruptcyRestrictions",
      "arrangementsWithCreditors",
      "assetsSequestrated",
      "involvedInProceedings",
      "financialObligations",
      "courtAdjudgedForMisconduct",
      "currentlyPartyToCivilProceedings",
      "awareOfCivilProceedings",
      "firmAdjudgedByCourt",
      "judgementDebtAganistFirm",
      "partyToCivilProceedingsAganistFirm",
      "firmPartOfcivilProceedings",
      "firmPartyToCivilProceedings",
      "woundUpFirm",
    ]),
    "7_03": hasYesInSection([
      "disqualifiedFromDirector",
      "subjectToProceedings",
      "subjectToInvestigation",
      "notifiedOfProceedings",
      "subjectOfInvestigationWithBusinessActivity",
      "refusedEntry",
      "complaintsAgainstYou",
      "arbitrationBoard",
    ]),
    "7_04": hasYesInSection([
      "suspendedByRegulatoryBody",
      "subjectOfDisciplinaryAction",
      "receivedWarning",
      "subjectOfInvestigationByRegulatoryBody",
      "requiredToProduceDocuments",
      "investigationByInspector",
      "ceasedOperating",
      "rejectedApplication",
      "subjectOfCivilAction",
      "paymentServices",
      "convictedOdCriminalOffence",
      "boardofDirectorsNotGrantedRelease",
      "carriedOnActivitiesWithoutAuthorization",
      "investigatedForCarryingOnActivities",
      "performedControlledFunctionWithoutApproval",
      "investigatedForControlledFunction",
      "failedToComplyWithRegulations",
      "subjectOfDisqualificationDirection",
    ]),
    "7_05": hasYesInSection([
      "conflictPerformanceofControlledFunctions",
      "qualifyingOwnership",
      "closeRelativesOwnershipShares",
      "closeRelativesFinancialRelations",
      "conflictOfInterest",
      "otherRelevantInformation",
    ]),
  };

  // Validate conditional additional information fields
  if (
    sectionHasYes["7_01"] &&
    (formData.additionalInformation_7_01 || "").trim() === ""
  ) {
    newErrors["additionalInformation_7_01"] = "This field is required.";
  }
  if (
    sectionHasYes["7_02"] &&
    (formData.additionalInformation_7_02 || "").trim() === ""
  ) {
    newErrors["additionalInformation_7_02"] = "This field is required.";
  }
  if (
    sectionHasYes["7_03"] &&
    (formData.additionalInformation_7_03 || "").trim() === ""
  ) {
    newErrors["additionalInformation_7_03"] = "This field is required.";
  }
  if (
    sectionHasYes["7_04"] &&
    (formData.additionalInformation_7_04 || "").trim() === ""
  ) {
    newErrors["additionalInformation_7_04"] = "This field is required.";
  }
  // if (
  //   sectionHasYes["7_05"] &&
  //   (formData.additionalInformation_7_05 || "").trim() === ""
  // ) {
  //   newErrors["additionalInformation_7_05"] = "This field is required.";
  // }

  for (const key in formData) {
    // Skip files arrays and additional information fields - they are handled separately above
    if (key === "files") continue;
    if (key === "additionalInformation") continue; // Skip standalone additionalInformation field
    if (key.startsWith("files_7_")) continue;
    if (key.startsWith("additionalInformation_7_")) continue;
    if (["candidateName", "candidateSignature", "date"].includes(key)) continue;

    // conditional: close relatives name required
    if (key === "nameOfCloseRelativesOwnershipShares") {
      if (
        formData.closeRelativesOwnershipShares === true &&
        (formData[key] || "").trim() === ""
      ) {
        newErrors[key] = "This field is required.";
      }
      continue;
    }

    if (key === "nameOfCloseRelativesFinancialRelations") {
      if (
        formData.closeRelativesFinancialRelations === true &&
        (formData[key] || "").trim() === ""
      ) {
        newErrors[key] = "This field is required.";
      }
      continue;
    }

    // conditional: qualifying ownership fields
    const qFields = [
      "companyName",
      "companyRegistrationNumber",
      "natureOfOperations",
      "registeredOffice",
      "possessionInPercentage",
    ];

    if (qFields.includes(key)) {
      if (
        formData.qualifyingOwnership === true &&
        (formData[key] || "").toString().trim() === ""
      ) {
        newErrors[key] = "This field is required.";
      }
      continue;
    }

    // signature field: required + canvas empty
    // if (key === "candidateSignature") {
    //   if (signatureIsEmpty) {
    //     // newErrors[key] = "Signature is required.";
    //   }
    //   continue;
    // }

    // generic required rule (matches your original intent)
    const v = formData[key];
    const isMissing =
      v === null ||
      v === undefined ||
      (typeof v === "string" && v.trim() === "");

    if (isMissing) {
      newErrors[key] = "This field is required.";
    }
  }

  errorTrackerV2.setSectionErrors("section7", newErrors);
  // console.log("Section 7 Validation - Errors found:", newErrors);
  return {
    ...newErrors,
    _isValid: Object.keys(newErrors).length === 0,
  };
}
